import type { HealthRecord, RecordDetail } from "@/shared/types";

// Supported MIME types
// doc/docx deliberately not accepted — medical records don't come in
// Word-document format, and this app never actually parsed them anyway
// (routed to the mock parser below, never sent to the backend).
export const SUPPORTED_FILE_TYPES = {
  // Documents
  "application/pdf": { extension: ".pdf", category: "document" },

  // Data formats
  "application/json": { extension: ".json", category: "data" },
  "text/json": { extension: ".json", category: "data" },
  "text/csv": { extension: ".csv", category: "data" },
  "application/csv": { extension: ".csv", category: "data" },
};

export const ACCEPTED_EXTENSIONS = ".pdf,.csv,.json,.xml,.ccd";

export interface ParsedHealthData {
  records: Partial<HealthRecord>[];
  rawData: unknown;
  parseErrors: string[];
  format: "json" | "xml" | "csv" | "sql" | "image" | "document" | "efax" | "direct_message";
}

// Parse JSON health records (supports FHIR, HL7 FHIR, and custom formats)
export function parseJSON(content: string): ParsedHealthData {
  const parseErrors: string[] = [];
  const records: Partial<HealthRecord>[] = [];
  
  try {
    const data = JSON.parse(content);
    
    // Check for FHIR Bundle format
    if (data.resourceType === "Bundle" && Array.isArray(data.entry)) {
      data.entry.forEach((entry: { resource?: Record<string, unknown> }, index: number) => {
        const resource = entry.resource;
        if (resource) {
          const record = parseFHIRResource(resource);
          if (record) records.push(record);
        }
      });
    }
    // Check for array of records
    else if (Array.isArray(data)) {
      data.forEach((item: Record<string, unknown>, index: number) => {
        const record = parseGenericHealthRecord(item);
        if (record) records.push(record);
      });
    }
    // Single record object
    else if (typeof data === "object") {
      const record = parseGenericHealthRecord(data);
      if (record) records.push(record);
    }
    
    return { records, rawData: data, parseErrors, format: "json" };
  } catch (e) {
    parseErrors.push(`JSON parse error: ${e instanceof Error ? e.message : "Unknown error"}`);
    return { records: [], rawData: null, parseErrors, format: "json" };
  }
}

// Parse FHIR resource to HealthRecord
function parseFHIRResource(resource: Record<string, unknown>): Partial<HealthRecord> | null {
  const resourceType = resource.resourceType as string;
  
  switch (resourceType) {
    case "DiagnosticReport":
      return {
        type: "lab_result",
        title: (resource.code as { text?: string })?.text || "Diagnostic Report",
        date: resource.effectiveDateTime as string || resource.issued as string || new Date().toISOString().split("T")[0],
        summary: (resource.conclusion as string) || "Diagnostic report from electronic health record.",
        status: mapFHIRStatus(resource.status as string),
        details: parseFHIRResults(resource.result as Array<{ display?: string; reference?: string }> || []),
      };
    case "Observation":
      return {
        type: "lab_result",
        title: (resource.code as { text?: string })?.text || "Observation",
        date: resource.effectiveDateTime as string || new Date().toISOString().split("T")[0],
        summary: `Value: ${formatFHIRValue(resource.valueQuantity as { value?: number; unit?: string })}`,
        status: mapFHIRStatus(resource.status as string),
        details: [{
          label: (resource.code as { text?: string })?.text || "Result",
          value: formatFHIRValue(resource.valueQuantity as { value?: number; unit?: string }),
          status: "normal",
          explanation: "Observation from health record",
        }],
      };
    case "MedicationRequest":
    case "MedicationStatement":
      return {
        type: "prescription",
        title: (resource.medicationCodeableConcept as { text?: string })?.text || "Medication",
        date: resource.authoredOn as string || new Date().toISOString().split("T")[0],
        summary: `Medication prescription from health record.`,
        status: "normal",
      };
    case "Immunization":
      return {
        type: "immunization",
        title: (resource.vaccineCode as { text?: string })?.text || "Immunization",
        date: resource.occurrenceDateTime as string || new Date().toISOString().split("T")[0],
        summary: "Immunization record from health record.",
        status: "normal",
      };
    case "Encounter":
      return {
        type: "visit_summary",
        title: (resource.type as Array<{ text?: string }>)?.[0]?.text || "Visit",
        date: (resource.period as { start?: string })?.start || new Date().toISOString().split("T")[0],
        summary: "Healthcare visit from health record.",
        status: "normal",
      };
    case "ImagingStudy":
      return {
        type: "imaging",
        title: (resource.description as string) || "Imaging Study",
        date: resource.started as string || new Date().toISOString().split("T")[0],
        summary: "Imaging study from health record.",
        status: "normal",
      };
    default:
      return null;
  }
}

function mapFHIRStatus(status: string): "normal" | "attention" | "critical" {
  const criticalStatuses = ["cancelled", "entered-in-error"];
  const attentionStatuses = ["preliminary", "amended"];
  
  if (criticalStatuses.includes(status)) return "critical";
  if (attentionStatuses.includes(status)) return "attention";
  return "normal";
}

function formatFHIRValue(valueQuantity: { value?: number; unit?: string } | undefined): string {
  if (!valueQuantity) return "N/A";
  return `${valueQuantity.value ?? "N/A"} ${valueQuantity.unit || ""}`.trim();
}

function parseFHIRResults(results: Array<{ display?: string; reference?: string }>): RecordDetail[] {
  return results.map((r) => ({
    label: r.display || "Result",
    value: "See details",
    status: "normal" as const,
    explanation: "Result from diagnostic report",
  }));
}

// Parse generic health record object
function parseGenericHealthRecord(data: Record<string, unknown>): Partial<HealthRecord> | null {
  // Try to extract common fields
  const title = (data.title || data.name || data.description || data.test_name || data.testName || "Health Record") as string;
  const date = (data.date || data.recordDate || data.created || data.timestamp || new Date().toISOString().split("T")[0]) as string;
  const type = inferRecordType(data);
  
  const details: RecordDetail[] = [];
  
  // Extract any numeric values as potential test results
  Object.entries(data).forEach(([key, value]) => {
    if (typeof value === "number" || (typeof value === "string" && !isNaN(parseFloat(value)))) {
      const formattedKey = key.replace(/([A-Z])/g, " $1").replace(/_/g, " ").trim();
      details.push({
        label: formattedKey.charAt(0).toUpperCase() + formattedKey.slice(1),
        value: String(value),
        status: "normal",
        explanation: `Value for ${formattedKey}`,
      });
    }
  });
  
  return {
    type,
    title: String(title),
    date: String(date).split("T")[0],
    summary: (data.summary || data.notes || data.description || "Imported health record") as string,
    status: "normal",
    details: details.length > 0 ? details : undefined,
  };
}

function inferRecordType(data: Record<string, unknown>): HealthRecord["type"] {
  const typeHints = JSON.stringify(data).toLowerCase();
  if (typeHints.includes("lab") || typeHints.includes("test") || typeHints.includes("result")) return "lab_result";
  if (typeHints.includes("prescription") || typeHints.includes("medication") || typeHints.includes("drug")) return "prescription";
  if (typeHints.includes("visit") || typeHints.includes("appointment") || typeHints.includes("encounter")) return "visit_summary";
  if (typeHints.includes("imaging") || typeHints.includes("xray") || typeHints.includes("mri") || typeHints.includes("ct")) return "imaging";
  if (typeHints.includes("vaccine") || typeHints.includes("immunization")) return "immunization";
  return "lab_result";
}

// Parse XML health records (supports CDA, CCR, and generic XML)
export function parseXML(content: string): ParsedHealthData {
  const parseErrors: string[] = [];
  const records: Partial<HealthRecord>[] = [];
  
  try {
    const parser = new DOMParser();
    const xmlDoc = parser.parseFromString(content, "text/xml");
    
    // Check for parse errors
    const parseError = xmlDoc.querySelector("parsererror");
    if (parseError) {
      parseErrors.push("XML parse error: " + parseError.textContent);
      return { records: [], rawData: null, parseErrors, format: "xml" };
    }
    
    const root = xmlDoc.documentElement;
    
    // Check for CDA (Clinical Document Architecture) format
    if (root.tagName === "ClinicalDocument" || root.querySelector("ClinicalDocument")) {
      const sections = xmlDoc.querySelectorAll("section");
      sections.forEach((section) => {
        const title = section.querySelector("title")?.textContent || "Section";
        const text = section.querySelector("text")?.textContent || "";
        
        records.push({
          type: inferTypeFromTitle(title),
          title,
          date: new Date().toISOString().split("T")[0],
          summary: text.substring(0, 200) + (text.length > 200 ? "..." : ""),
          status: "normal",
        });
      });
    }
    // Check for CCR (Continuity of Care Record) format
    else if (root.tagName === "ContinuityOfCareRecord" || root.querySelector("ContinuityOfCareRecord")) {
      const body = xmlDoc.querySelector("Body");
      if (body) {
        ["Problems", "Medications", "Allergies", "Procedures", "Results", "Encounters", "Immunizations"].forEach(sectionName => {
          const section = body.querySelector(sectionName);
          if (section) {
            const items = section.children;
            for (let i = 0; i < items.length; i++) {
              const item = items[i];
              const description = item.querySelector("Description")?.textContent || item.querySelector("Text")?.textContent || sectionName;
              const date = item.querySelector("DateTime")?.textContent || item.querySelector("ExactDateTime")?.textContent;
              
              records.push({
                type: mapCCRSectionToType(sectionName),
                title: description,
                date: date?.split("T")[0] || new Date().toISOString().split("T")[0],
                summary: `${sectionName} record from CCR document`,
                status: "normal",
              });
            }
          }
        });
      }
    }
    // Generic XML parsing
    else {
      const record = parseGenericXML(root);
      if (record) records.push(record);
    }
    
    return { records, rawData: content, parseErrors, format: "xml" };
  } catch (e) {
    parseErrors.push(`XML parse error: ${e instanceof Error ? e.message : "Unknown error"}`);
    return { records: [], rawData: null, parseErrors, format: "xml" };
  }
}

function inferTypeFromTitle(title: string): HealthRecord["type"] {
  const lowerTitle = title.toLowerCase();
  if (lowerTitle.includes("lab") || lowerTitle.includes("result")) return "lab_result";
  if (lowerTitle.includes("medication") || lowerTitle.includes("prescription")) return "prescription";
  if (lowerTitle.includes("encounter") || lowerTitle.includes("visit")) return "visit_summary";
  if (lowerTitle.includes("imaging") || lowerTitle.includes("radiology")) return "imaging";
  if (lowerTitle.includes("immunization") || lowerTitle.includes("vaccine")) return "immunization";
  return "lab_result";
}

function mapCCRSectionToType(section: string): HealthRecord["type"] {
  switch (section) {
    case "Medications": return "prescription";
    case "Immunizations": return "immunization";
    case "Encounters": return "visit_summary";
    case "Results": return "lab_result";
    case "Procedures": return "imaging";
    default: return "lab_result";
  }
}

function parseGenericXML(element: Element): Partial<HealthRecord> | null {
  const title = element.querySelector("title, name, description")?.textContent || element.tagName;
  const date = element.querySelector("date, datetime, timestamp")?.textContent;
  const details: RecordDetail[] = [];
  
  // Extract text content from child elements
  const children = element.children;
  for (let i = 0; i < Math.min(children.length, 10); i++) {
    const child = children[i];
    if (child.textContent && child.textContent.trim()) {
      details.push({
        label: child.tagName.replace(/([A-Z])/g, " $1").trim(),
        value: child.textContent.trim().substring(0, 100),
        status: "normal",
        explanation: `Data from ${child.tagName} field`,
      });
    }
  }
  
  return {
    type: "lab_result",
    title,
    date: date?.split("T")[0] || new Date().toISOString().split("T")[0],
    summary: "Health record imported from XML document",
    status: "normal",
    details: details.length > 0 ? details : undefined,
  };
}

// Parse CSV health records
export function parseCSV(content: string): ParsedHealthData {
  const parseErrors: string[] = [];
  const records: Partial<HealthRecord>[] = [];
  
  try {
    const lines = content.split(/\r?\n/).filter(line => line.trim());
    if (lines.length < 2) {
      parseErrors.push("CSV file must have at least a header row and one data row");
      return { records: [], rawData: null, parseErrors, format: "csv" };
    }
    
    // Parse header
    const headers = parseCSVLine(lines[0]);
    
    // Parse data rows
    for (let i = 1; i < lines.length; i++) {
      const values = parseCSVLine(lines[i]);
      if (values.length !== headers.length) {
        parseErrors.push(`Row ${i + 1}: Column count mismatch`);
        continue;
      }
      
      const rowData: Record<string, string> = {};
      headers.forEach((header, idx) => {
        rowData[header.toLowerCase().trim()] = values[idx];
      });
      
      // Map common CSV column names to health record fields
      const record: Partial<HealthRecord> = {
        type: inferRecordType(rowData as Record<string, unknown>),
        title: rowData.test || rowData.name || rowData.title || rowData.description || `Record ${i}`,
        date: (rowData.date || rowData.test_date || rowData.record_date || new Date().toISOString()).split("T")[0],
        provider: rowData.provider || rowData.doctor || rowData.physician,
        facility: rowData.facility || rowData.lab || rowData.hospital || rowData.clinic,
        summary: rowData.summary || rowData.notes || rowData.result || "CSV imported record",
        status: mapCSVStatus(rowData.status || rowData.flag || ""),
        details: extractCSVDetails(rowData, headers),
      };
      
      records.push(record);
    }
    
    return { records, rawData: { headers, rowCount: lines.length - 1 }, parseErrors, format: "csv" };
  } catch (e) {
    parseErrors.push(`CSV parse error: ${e instanceof Error ? e.message : "Unknown error"}`);
    return { records: [], rawData: null, parseErrors, format: "csv" };
  }
}

function parseCSVLine(line: string): string[] {
  const result: string[] = [];
  let current = "";
  let inQuotes = false;
  
  for (let i = 0; i < line.length; i++) {
    const char = line[i];
    
    if (char === '"') {
      if (inQuotes && line[i + 1] === '"') {
        current += '"';
        i++;
      } else {
        inQuotes = !inQuotes;
      }
    } else if (char === "," && !inQuotes) {
      result.push(current.trim());
      current = "";
    } else {
      current += char;
    }
  }
  
  result.push(current.trim());
  return result;
}

function mapCSVStatus(status: string): "normal" | "attention" | "critical" {
  const lower = status.toLowerCase();
  if (lower.includes("critical") || lower.includes("abnormal") || lower.includes("high") || lower.includes("low")) {
    return lower.includes("critical") ? "critical" : "attention";
  }
  return "normal";
}

function extractCSVDetails(rowData: Record<string, string>, headers: string[]): RecordDetail[] {
  const skipFields = ["test", "name", "title", "description", "date", "test_date", "record_date", 
                      "provider", "doctor", "physician", "facility", "lab", "hospital", "clinic",
                      "summary", "notes", "status", "flag"];
  
  return headers
    .filter(h => !skipFields.includes(h.toLowerCase().trim()) && rowData[h.toLowerCase().trim()])
    .map(h => ({
      label: h.replace(/_/g, " ").replace(/\b\w/g, l => l.toUpperCase()),
      value: rowData[h.toLowerCase().trim()],
      status: "normal" as const,
      explanation: `Value from ${h} column`,
    }))
    .slice(0, 10);
}

// Parse SQL health records (extracts data from INSERT statements)
export function parseSQL(content: string): ParsedHealthData {
  const parseErrors: string[] = [];
  const records: Partial<HealthRecord>[] = [];
  
  try {
    // Extract INSERT statements
    const insertRegex = /INSERT\s+INTO\s+[`"']?(\w+)[`"']?\s*\(([^)]+)\)\s*VALUES\s*\(([^)]+)\)/gi;
    let match;
    
    while ((match = insertRegex.exec(content)) !== null) {
      const tableName = match[1];
      const columns = match[2].split(",").map(c => c.replace(/[`"'\s]/g, "").toLowerCase());
      const values = parseCSVLine(match[3]).map(v => v.replace(/^['"]|['"]$/g, ""));
      
      const rowData: Record<string, string> = {};
      columns.forEach((col, idx) => {
        rowData[col] = values[idx] || "";
      });
      
      // Check if this looks like health data
      const isHealthTable = ["patients", "records", "labs", "results", "medications", 
                            "prescriptions", "visits", "immunizations", "tests"].some(
        t => tableName.toLowerCase().includes(t)
      );
      
      if (isHealthTable || columns.some(c => ["patient", "result", "test", "medication", "diagnosis"].includes(c))) {
        const record: Partial<HealthRecord> = {
          type: inferRecordType(rowData as Record<string, unknown>),
          title: rowData.name || rowData.test_name || rowData.title || rowData.description || `${tableName} Record`,
          date: (rowData.date || rowData.created_at || rowData.record_date || new Date().toISOString()).split("T")[0],
          provider: rowData.provider || rowData.doctor,
          facility: rowData.facility || rowData.lab,
          summary: rowData.summary || rowData.notes || `Record from ${tableName} table`,
          status: mapCSVStatus(rowData.status || ""),
          details: extractSQLDetails(rowData, columns),
        };
        
        records.push(record);
      }
    }
    
    // Also try to parse SELECT query results format (if it's a dump)
    const selectResultRegex = /\|\s*([^|]+(?:\|[^|]+)*)\s*\|/g;
    let headerRow: string[] | null = null;
    
    while ((match = selectResultRegex.exec(content)) !== null) {
      const cells = match[1].split("|").map(c => c.trim()).filter(c => c && !c.match(/^-+$/));
      
      if (!headerRow) {
        headerRow = cells.map(c => c.toLowerCase());
      } else if (cells.length === headerRow.length) {
        const rowData: Record<string, string> = {};
        headerRow.forEach((h, idx) => {
          rowData[h] = cells[idx];
        });
        
        const record: Partial<HealthRecord> = {
          type: inferRecordType(rowData as Record<string, unknown>),
          title: rowData.name || rowData.test || rowData.title || "SQL Record",
          date: (rowData.date || new Date().toISOString()).split("T")[0],
          summary: rowData.summary || rowData.notes || "Record from SQL query result",
          status: "normal",
          details: extractSQLDetails(rowData, headerRow),
        };
        
        records.push(record);
      }
    }
    
    if (records.length === 0) {
      parseErrors.push("No health records found in SQL file. Ensure the file contains INSERT statements or query results for health-related tables.");
    }
    
    return { records, rawData: content, parseErrors, format: "sql" };
  } catch (e) {
    parseErrors.push(`SQL parse error: ${e instanceof Error ? e.message : "Unknown error"}`);
    return { records: [], rawData: null, parseErrors, format: "sql" };
  }
}

function extractSQLDetails(rowData: Record<string, string>, columns: string[]): RecordDetail[] {
  const skipFields = ["id", "patient_id", "user_id", "created_at", "updated_at", "deleted_at",
                      "name", "test_name", "title", "description", "date", "record_date",
                      "provider", "doctor", "facility", "lab", "summary", "notes", "status"];
  
  return columns
    .filter(c => !skipFields.includes(c) && rowData[c])
    .map(c => ({
      label: c.replace(/_/g, " ").replace(/\b\w/g, l => l.toUpperCase()),
      value: rowData[c],
      status: "normal" as const,
      explanation: `Value from ${c} field`,
    }))
    .slice(0, 10);
}

// Parse file based on type
export async function parseHealthFile(file: File): Promise<ParsedHealthData> {
  const extension = file.name.split(".").pop()?.toLowerCase() || "";
  const mimeType = file.type.toLowerCase();
  
  // Data format files
  if (extension === "json" || mimeType.includes("json")) {
    const content = await file.text();
    return parseJSON(content);
  }
  
  if (extension === "xml" || mimeType.includes("xml")) {
    const content = await file.text();
    return parseXML(content);
  }
  
  if (extension === "csv" || mimeType.includes("csv")) {
    const content = await file.text();
    return parseCSV(content);
  }
  
  if (extension === "sql" || mimeType.includes("sql")) {
    const content = await file.text();
    return parseSQL(content);
  }
  
  // TIFF/Image files (for scanned documents, eFax)
  if (["tif", "tiff"].includes(extension) || mimeType.includes("tiff")) {
    return {
      records: [{
        type: "lab_result",
        title: file.name.replace(/\.[^/.]+$/, ""),
        date: new Date().toISOString().split("T")[0],
        summary: "TIFF image file uploaded. OCR processing would extract text content.",
        status: "normal",
      }],
      rawData: { fileType: "tiff", size: file.size },
      parseErrors: [],
      format: "image",
    };
  }
  
  // Regular images
  if (mimeType.startsWith("image/")) {
    return {
      records: [{
        type: "imaging",
        title: file.name.replace(/\.[^/.]+$/, ""),
        date: new Date().toISOString().split("T")[0],
        summary: "Image file uploaded. Document analysis would extract health information.",
        status: "normal",
      }],
      rawData: { fileType: "image", size: file.size },
      parseErrors: [],
      format: "image",
    };
  }
  
  // PDF and other documents
  return {
    records: [{
      type: "lab_result",
      title: file.name.replace(/\.[^/.]+$/, ""),
      date: new Date().toISOString().split("T")[0],
      summary: "Document uploaded. Text extraction would process the content.",
      status: "normal",
    }],
    rawData: { fileType: mimeType || extension, size: file.size },
    parseErrors: [],
    format: "document",
  };
}
